import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse
} from '@mui/material';
import {
  Business,
  AccessTime,
  ArrowForward,
  Description,
  LocationOn,
  CompareArrows,
  ShoppingCart,
  FilterList,
  ExpandMore,
  ExpandLess,
  Clear
} from '@mui/icons-material';
import api from '../services/api';
import { brand, bg } from '../theme/colors';

const ClientQuoteRequestsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'sent', 'quotes_received', 'expired'
  const [orderStatusFilter, setOrderStatusFilter] = useState('all'); // 'all', 'active', 'completed', 'picked_up', 'delivered', 'confirmed'
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadQuoteRequests();
  }, [statusFilter]);

  const loadQuoteRequests = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await api.get('/merchant-quotes/requests', { params });

      if (response.data.success) {
        setQuoteRequests(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading quote requests:', err);
      setError('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChipSx = (status) => {
    switch (status) {
      case 'sent':
        return { bgcolor: '#eff6ff', color: brand.primary, border: '1px solid #bfdbfe' };
      case 'quotes_received':
        return { bgcolor: '#f0fdf4', color: '#43B581', border: '1px solid #bbf7d0' };
      case 'expired':
        return { bgcolor: '#fdf2f2', color: '#e74c3c', border: '1px solid #fecaca' };
      default:
        return {};
    }
  };

  const getOrderStatusChipSx = (status) => {
    switch (status) {
      case 'picked_up':
        return { bgcolor: '#fffbeb', color: '#FAA61A', border: '1px solid #fde68a' };
      case 'delivered':
        return { bgcolor: '#eff6ff', color: brand.primary, border: '1px solid #bfdbfe' };
      case 'completed':
        return { bgcolor: '#f0fdf4', color: '#43B581', border: '1px solid #bbf7d0' };
      case 'confirmed':
        return { bgcolor: '#eff6ff', color: brand.primary, border: '1px solid #bfdbfe' };
      default:
        return {};
    }
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return 'N/A';
    const date = new Date(deadline);
    const now = new Date();
    const diff = date - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'Expired';
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `${days} days remaining`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Material Quote Requests
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ mb: 4, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Quote Request Status</InputLabel>
            <Select
              value={statusFilter}
              label="Quote Request Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Requests</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="quotes_received">Quotes Received</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={orderStatusFilter}
              label="Order Status"
              onChange={(e) => setOrderStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Orders</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="picked_up">Picked Up</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>

          {(statusFilter !== 'all' || orderStatusFilter !== 'all') && (
            <Button
              variant="text"
              color="error"
              onClick={() => {
                setStatusFilter('all');
                setOrderStatusFilter('all');
              }}
              startIcon={<Clear />}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {(() => {
        // Filter quote requests based on order status filter
        let filteredRequests = quoteRequests;
        
        if (orderStatusFilter !== 'all') {
          filteredRequests = quoteRequests.filter(request => {
            if (!request.orders || request.orders.length === 0) {
              // If no orders, only show if filter is 'active' (meaning no orders = active)
              return orderStatusFilter === 'active';
            }
            
            if (orderStatusFilter === 'active') {
              // Show requests with at least one non-completed order
              return request.orders.some(o => o.order_status !== 'completed');
            } else {
              // Show requests with at least one order matching the filter
              return request.orders.some(o => o.order_status === orderStatusFilter);
            }
          });
        }

        return filteredRequests.length === 0 ? (
          <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No quote requests found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {orderStatusFilter !== 'all' 
                ? `No quote requests with ${orderStatusFilter.replace('_', ' ')} orders.`
                : 'Create a new quote request from your project page to get material quotes from suppliers.'}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {filteredRequests.map((request) => (
            <Grid item xs={12} key={request.id}>
              <Card
                sx={{
                  '&:hover': { boxShadow: 4 },
                  transition: 'box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {request.project_title || 'Project'}
                      </Typography>
                      <Chip
                        label={request.status === 'quotes_received' ? 'Quotes Received' :
                               request.status === 'sent' ? 'Awaiting Quotes' :
                               request.status || 'Unknown'}
                        size="small"
                        sx={{ mr: 1, ...getStatusChipSx(request.status) }}
                      />
                      {request.quotes_received_count > 0 && (
                        <Chip
                          label={`${request.quotes_received_count} Quote${request.quotes_received_count > 1 ? 's' : ''}`}
                          size="small"
                          sx={{ mr: 1, bgcolor: '#f0fdf4', color: '#43B581', border: '1px solid #bbf7d0' }}
                        />
                      )}
                      {request.orders && request.orders.filter(o => o.order_status !== 'completed').length > 0 && (
                        <Chip
                          label={`${request.orders.filter(o => o.order_status !== 'completed').length} Active Order${request.orders.filter(o => o.order_status !== 'completed').length > 1 ? 's' : ''}`}
                          size="small"
                          sx={{ bgcolor: '#eff6ff', color: brand.primary, border: '1px solid #bfdbfe' }}
                        />
                      )}
                      {request.orders && request.orders.filter(o => o.order_status === 'completed').length > 0 && (
                        <Chip
                          label={`${request.orders.filter(o => o.order_status === 'completed').length} Completed`}
                          size="small"
                          sx={{ bgcolor: '#f0fdf4', color: '#43B581', border: '1px solid #bbf7d0' }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {request.quotes_received_count > 0 && (
                        <Button
                          variant="contained"
                          startIcon={<CompareArrows />}
                          onClick={() => navigate(`/quotes/${request.id}`)}
                          sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
                        >
                          View Quotes
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        startIcon={<Description />}
                        onClick={() => navigate(`/projects/${request.project_id}`)}
                        sx={{ borderColor: brand.primary, color: brand.primary, borderWidth: 2, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
                      >
                        View Project
                      </Button>
                    </Box>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {typeof request.delivery_address === 'object' 
                            ? request.delivery_address?.city || request.delivery_address?.address || 'N/A'
                            : request.delivery_address || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          Deadline: {formatDeadline(request.quote_deadline)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Request Type: {request.request_type === 'boq_upload' ? 'BOQ Upload' : 'Material Form'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Categories: {request.material_categories?.join(', ') || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Sent to: {request.selected_merchant_ids?.length || 0} merchant(s)
                      </Typography>
                    </Grid>
                  </Grid>

                  {request.boq_file_url && (
                    <Button
                      size="small"
                      startIcon={<Description />}
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(request.boq_file_url, '_blank');
                      }}
                      sx={{ mr: 1, borderColor: brand.primary, color: brand.primary, borderWidth: 2, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
                    >
                      View BOQ
                    </Button>
                  )}

                  {/* Show order status if orders exist (excluding completed orders) */}
                  {request.orders && request.orders.filter(o => o.order_status !== 'completed').length > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: bg.subtle, borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Active Orders:
                      </Typography>
                      {request.orders.filter(o => o.order_status !== 'completed').map((order, idx) => {
                        const getOrderStatusColor = (status) => {
                          switch (status) {
                            case 'picked_up': return 'warning';
                            case 'delivered': return 'info';
                            case 'completed': return 'success';
                            case 'confirmed': return 'primary';
                            default: return 'default';
                          }
                        };
                        
                        const getOrderStatusLabel = (status) => {
                          switch (status) {
                            case 'picked_up': return 'Picked Up';
                            case 'delivered': return 'Delivered';
                            case 'completed': return 'Completed';
                            case 'confirmed': return 'Confirmed';
                            default: return status?.replace('_', ' ') || 'Unknown';
                          }
                        };
                        
                        return (
                          <Box key={order.order_id || idx} sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">
                                Order #{order.order_id?.substring(0, 20)}...
                              </Typography>
                              <Chip
                                label={getOrderStatusLabel(order.order_status)}
                                size="small"
                                sx={getOrderStatusChipSx(order.order_status)}
                              />
                            </Box>
                            {order.order_status === 'picked_up' || order.order_status === 'delivered' ? (
                              <Box sx={{ mt: 1 }}>
                                <Alert severity="warning" sx={{ fontSize: '0.75rem', mb: 1 }}>
                                  {order.delivery_confirmed_by_client 
                                    ? 'You have confirmed receipt. Payment will be released.'
                                    : 'Please confirm receipt within 72 business hours or payment will be auto-released.'}
                                </Alert>
                                {!order.delivery_confirmed_by_client && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => navigate(`/orders/${order.order_id}/delivery/confirm`)}
                                    sx={{ fontSize: '0.7rem', py: 0.5, bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa870' } }}
                                  >
                                    Confirm Delivery
                                  </Button>
                                )}
                              </Box>
                            ) : null}
                            {order.actual_delivery_date && (
                              <Typography variant="caption" color="text.secondary">
                                {order.delivery_method === 'pickup' ? 'Picked up' : 'Delivered'} on: {new Date(order.actual_delivery_date).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    Created: {new Date(request.created_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            ))}
          </Grid>
        );
      })()}
    </Container>
  );
};

export default ClientQuoteRequestsPage;

